import * as _ from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isBrowser, isMobile } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import * as elasticSearchActions from "../../actions/elasticSearchActions";
import { SearchInputMobile, SearchInputWeb } from "../../components/Search";
import MobileSearchResults from "../../components/Search/MobileSearch/MobileSearchResults";
import WebSearchResults from "../../components/Search/WebSearchResults/WebSearchResults";
import LocationSearchModal from "../../modals/LocationSearch/LocationSearchModal";

import "react-loading-skeleton/dist/skeleton.css";
import { SearchPageTypes } from "../../App";
import config from "../../config";
import { logEvents } from "../../eventsManager";
import {
  ALL,
  BENEFITS,
  BLOGS,
  BRANCHES,
  BRANDS,
  EMPLOYERS,
  RECIPES,
  RESTAURANTS,
  VF_ENTITIES_TYPE,
  getVFEntityByName,
} from "../../resources/config";
import { ENTITY_TAGS } from "../../resources/entities/entityTags";
import icons from "../../resources/icons";
import { linkToSearch } from "../../utils/links";
import classes from "./Search.module.css";
import { resultsFilter, resultsPreparation, sectionsRender } from "./SearchHelpers";

const getEntitiesTypeList = (results) => {
  if (!results) return null;

  const typeList = [
    {
      name: ALL,
      label: "all",
      icon: icons.allIcon,
      count: 0,
    },
    {
      name: RESTAURANTS,
      label: "restaurants",
      icon: icons.restaurantsEntity,
      count: results.counts?.restaurants || 0,
    },
    {
      name: BENEFITS,
      label: "benefits",
      icon: icons.benefitsEntity,
      count: results.counts?.benefits || 0,
    },
    {
      name: RECIPES,
      label: "recipes",
      icon: icons.recipesEntity,
      count: results.counts?.recipes || 0,
    },
    {
      name: BRANCHES,
      label: "branches",
      icon: icons.businessesEntity,
      count: results.counts?.branches || 0,
    },
    {
      name: BRANDS,
      label: "companies",
      icon: icons.productsEntity,
      count: results.counts?.companies || 0,
    },
    {
      name: BLOGS,
      label: "blogs",
      icon: icons.blogsEntity,
      count: results.counts?.blogs || 0,
    },
    {
      name: EMPLOYERS,
      label: "employers",
      icon: icons.employersEntity,
      count: results.counts?.employers || 0,
    },
  ];

  // Calculate total count for "all" option
  typeList[0].count = typeList.slice(1).reduce((sum, type) => sum + type.count, 0);
  return typeList;
};

const SearchElastic = ({ type, entity }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Path parameters - for routes like /search/restaurants/pizza or /search/business/123
  const { filterName, chainId, busId, suggestOverride: suggestOverrideParam } = useParams();

  // Query parameters - for routes like /search?entity=restaurants&input=pizza
  const [urlParams] = useSearchParams();

  // Redux state
  const isSearchResultRequested = useSelector((state) => state.entities.isSearchResultRequested);
  const searchResults = useSelector((state) => state.entities.searchResults);
  const userLocation = useSelector((state) => state.user.userLocation);
  const entityCategories = useSelector((state) => state.entities.entityCategories);
  const news = useSelector((state) => state.entities.newsEntities);
  const entityTags = useSelector((state) => state.entities.entityTags);
  const testConfig = useSelector((state) =>
    state.elasticSearch.testConfigEnabled ? state.elasticSearch.testConfig : null
  );

  // Core search state
  const [searchParams, setSearchParams] = useState(() => getEmptySearchParams());
  const [selectedEntity, setSelectedEntity] = useState(VF_ENTITIES_TYPE[0]);
  const [suggestOverride, setSuggestOverride] = useState(false);
  const [isLoading, setLoading] = useState(true);

  // Search results state
  const [preparedResults, setPreparedResults] = useState(null);
  const [sectionList, setSectionList] = useState(null);
  const [hasResults, setHasResults] = useState(true);
  const [totalPages, setTotalPages] = useState(1);

  // Count state for filters
  const [counterEntities, setCounterEntities] = useState(null);
  const [countedCategories, setCountedCategories] = useState(null);
  const [countedTags, setCountedTags] = useState(null);

  // UI state
  const [isResultsOpen, setResultsOpen] = useState(false);
  const [isLocationModalVisible, setLocationModalVisible] = useState(false);
  const [isInputVisible, setInputVisible] = useState(true);
  const [entityLocations, setEntityLocations] = useState(null);

  // Temporary state for handling URL params
  const [unhandledCategoryParams, setUnhandledCategoryParams] = useState(null);
  const [isUrlParamsProcessed, setUrlParamsProcessed] = useState(false);

  // Refs
  const bottomSheetRef = useRef();

  // Constants
  const pageSize = isBrowser ? 24 : 25;

  function getEmptySearchParams() {
    return {
      enteredInput: "",
      selectedTags: [],
      selectedCategories: [],
      selectedChainId: null,
      selectedBusinessId: null,
      currentPage: 1,
    };
  }

  // Memoized search input for comparison
  const currentSearchInput = useMemo(
    () => ({
      searchParams,
      location: userLocation,
      testConfig,
      pageSize,
    }),
    [searchParams, userLocation, testConfig, pageSize]
  );

  // Check if search should be skipped
  const shouldSkipSearch = useMemo(() => {
    return _.isEqual(currentSearchInput, searchResults?.input);
  }, [currentSearchInput, searchResults?.input]);

  //we have this to be compatible with older components, see bellow
  const searchParamsWithSelectedEntity = useMemo(() => {
    return {
      ...searchParams,
      selectedEntity,
    };
  }, [searchParams, selectedEntity]);

  // Effects
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [urlParams]);

  // Initialize empty section list when selected entity changes
  useEffect(() => {
    setSectionList(sectionsRender.renderEmptySectionList(selectedEntity));
  }, [selectedEntity]);

  // Main search effect - triggers Elasticsearch search
  useEffect(() => {
    if (!isSearchResultRequested && entityCategories && userLocation && isUrlParamsProcessed) {
      // Skip search if input matches previous search
      if (shouldSkipSearch) {
        console.log("skipping search - input matches previous search", {
          current: currentSearchInput,
          previous: searchResults?.input,
        });
        return;
      }
      console.log("searching elasticsearch, searchParams", searchParams);
      elasticSearchActions.getSearchResultsElastic(
        dispatch,
        userLocation,
        searchParams,
        entityCategories,
        pageSize,
        testConfig
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldSkipSearch,
    isSearchResultRequested,
    entityCategories,
    pageSize,
    testConfig,
    dispatch,
    isUrlParamsProcessed,
  ]);

  // Reset loading state when search is requested
  useEffect(() => {
    if (isSearchResultRequested) {
      setSectionList(sectionsRender.renderEmptySectionList(selectedEntity));
      setPreparedResults(null);
      setLoading(true);
    }
  }, [isSearchResultRequested, selectedEntity]);

  const getResultsNumber = useCallback(() => {
    if (counterEntities) {
      const entity = counterEntities.find((entity) => entity.name === selectedEntity.name);
      return entity?.count || 0;
    }
    return 0;
  }, [counterEntities, selectedEntity.name]);

  //_____________________________________________________________________________//
  /// 1 . Handling URL params

  const handlerRegularSearchParams = useCallback(
    (newSearchParams) => {
      //Getting params from page url
      const entityParams = getVFEntityByName(urlParams.get("entity"));
      const categoryParams = urlParams.get("categories");
      const categoriesArray = categoryParams ? categoryParams.split("-") : [];
      const categories = handleSelectedCategoryByParams(categoriesArray, entityCategories, entityParams);
      newSearchParams.selectedCategories = categories;

      let tagsParams = urlParams.get("tags");
      const tagsArray = tagsParams ? tagsParams.split("-") : [];
      const tags = handleSelectedTagByParams(tagsArray);

      newSearchParams.selectedTags = tags;

      const inputParam = urlParams.get("input");

      newSearchParams.enteredInput = inputParam ? inputParam : "";

      const chainId = urlParams.get("chainId");
      newSearchParams.selectedChainId = chainId;

      const businessId = urlParams.get("businessId");
      newSearchParams.selectedBusinessId = businessId;

      const page = urlParams.get("page");
      newSearchParams.currentPage = page ? page : 1;

      const suggestOverrideParam = urlParams.get("suggestOverride");

      return { entityParams, suggestOverride: suggestOverrideParam };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [urlParams, entityCategories]
  );

  useEffect(() => {
    var newSearchParams = getEmptySearchParams();
    let newSelectedEntity = VF_ENTITIES_TYPE[0];
    let newSuggestOverride = false;

    switch (type) {
      case SearchPageTypes.SEARCH:
        const result = handlerRegularSearchParams(newSearchParams);
        newSelectedEntity = result.entityParams;
        newSuggestOverride = result.suggestOverride;
        break;
      case SearchPageTypes.ALIAS_ENTITY:
        newSelectedEntity = getVFEntityByName(entity);
        if (filterName) {
          // first search for category, if not found then search for tag
          const entityObject = getVFEntityByName(entity);
          newSelectedEntity = entityObject;
          const categories = handleSelectedCategoryByParams([filterName], entityCategories, entityObject);
          if (categories && categories.length > 0) {
            newSearchParams.selectedCategories = categories;
          } else {
            const tags = handleSelectedTagByParams([filterName]);
            newSearchParams.selectedTags = tags;
          }
        }
        break;
      case SearchPageTypes.ALIAS_BUSINESS:
        newSelectedEntity = getVFEntityByName(BRANCHES);
        newSearchParams.selectedBusinessId = busId;
        break;
      case SearchPageTypes.ALIAS_CHAIN:
        newSelectedEntity = getVFEntityByName(RESTAURANTS);
        newSearchParams.selectedChainId = chainId;
        break;
      default:
        newSuggestOverride = suggestOverrideParam;
        break;
    }
    setSearchParams(newSearchParams);
    setSelectedEntity(newSelectedEntity);
    setSuggestOverride(newSuggestOverride);
    setUrlParamsProcessed(true);
    logSelectedFilters(newSearchParams, newSelectedEntity);
    setSectionList(sectionsRender.renderEmptySectionList(newSelectedEntity));
  }, [urlParams]);

  const logSelectedFilters = (searchParams, selectedEntity) => {
    const params = {
      entity: selectedEntity.name,
      query_text: searchParams.enteredInput,
      tags: searchParams.selectedTags.map((t) => t.name),
      categories: searchParams.selectedCategories.map((c) => c.category_name),
    };
    logEvents("search_results_displayed", params);
  };

  //_____________________________________________________________________________//
  const prepareSearchResults = useCallback(() => {
    if (!searchResults || !entityCategories || !entityTags || !userLocation?.coords) return;

    const preparedResults = { ...searchResults };
    resultsPreparation.prepareSearchResults(preparedResults, entityCategories, entityTags, userLocation.coords);
    setPreparedResults(preparedResults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults, entityCategories, entityTags, userLocation]);

  //_____________________________________________________________________________//
  /// All data is received from the server

  useEffect(() => {
    if (userLocation?.coords && searchResults && entityCategories && entityTags && !isSearchResultRequested) {
      if (unhandledCategoryParams) {
        const entityParams = entity ? getVFEntityByName(entity) : getVFEntityByName(urlParams.get("entity"));
        const categories = handleSelectedCategoryByParams(unhandledCategoryParams, entityCategories, entityParams);
        // Use proper state update instead of direct mutation
        setSearchParams((prev) => ({
          ...prev,
          selectedCategories: categories,
        }));
        setUnhandledCategoryParams(null); // Clear after handling
      }
      prepareSearchResults();
    }
  }, [
    searchResults,
    entityCategories,
    entityTags,
    userLocation,
    isSearchResultRequested,
    prepareSearchResults,
    unhandledCategoryParams,
    entity,
    urlParams,
  ]);

  //_____________________________________________________________________________//
  /// Process prepared results and update UI state
  useEffect(() => {
    if (!preparedResults) return;

    const { categoriesCount, computedTagsCounts } = preparedResults; // only available in elasticsearch

    // Get tags structure and set count to "?" since ElasticSearch doesn't return tag counts
    const emptyTagsCount = resultsFilter.getEmptyTagsCount(selectedEntity?.name);
    const countedTags = Object.values(emptyTagsCount).map((tag) => ({
      ...tag,
      count: computedTagsCounts?.[tag.name] ?? "?",
    }));

    // Update all count states
    setCounterEntities(getEntitiesTypeList(preparedResults));
    setCountedCategories(categoriesCount);
    setCountedTags(countedTags);

    // Prepare sections for rendering
    const { sectionList, locationList } = sectionsRender.preparePageSections(
      preparedResults,
      selectedEntity,
      1, // Show all results, don't slice
      pageSize,
      isMobile
    );

    setSectionList(sectionList);
    setEntityLocations(locationList);
    setHasResults(preparedResults.total > 0);
    setTotalPages(preparedResults.pages);
    setLoading(false);
  }, [preparedResults, selectedEntity, pageSize]);

  // };

  //_____________________________________________________________________________//

  const handleSelectedCategoryByParams = (categoryNames, entityCategories, selectedEntity) => {
    const categories = [];
    if (entityCategories && categoryNames) {
      categoryNames.forEach((categoryName) => {
        let category;
        switch (selectedEntity.name) {
          case RESTAURANTS:
            category = entityCategories.restaurants.find((category) => category.category_name === categoryName);
            if (category) categories.push(category);
            break;
          case BRANCHES:
            category = entityCategories.businessesSubs.find((category) => category.category_name === categoryName);
            if (category) categories.push(category);
            break;
          case RECIPES:
            category = entityCategories.recipes.find((category) => category.category_name === categoryName);
            if (category) categories.push(category);
            break;
          case BENEFITS:
            category = entityCategories.benefits.find((category) => category.category_name === categoryName);
            if (category) categories.push(category);
            break;
          case BRANDS:
            category = entityCategories.companiesSubs.find((category) => category.category_name === categoryName);
            if (category) categories.push(category);
            break;
          case BLOGS:
            category = entityCategories.blogs.find((category) => category.category_name === categoryName);
            if (category) categories.push(category);
            break;
          case EMPLOYERS:
            category = entityCategories.employers.find((category) => category.category_name === categoryName);
            if (category) categories.push(category);
            break;
          default: //do nothing
        }
      });
    } else if (categoryNames) {
      setUnhandledCategoryParams(categoryNames);
    }
    return categories;
  };

  const handleSelectedTagByParams = (tagNames) => {
    const tags = [];
    tagNames.forEach((tagName) => {
      const tag = ENTITY_TAGS[tagName];
      if (tag) tags.push(ENTITY_TAGS[tagName]);
    });
    return tags;
  };

  const entitySelectHandler = (entity, _page = searchParams.currentPage) => {
    const entityName = entity === "all" ? null : entity;
    navigate(
      linkToSearch({
        entityType: entityName,
        input: searchParams.enteredInput,
        page: 1, // Reset page, or use searchParams.currentPage, ?
        suggestOverride: true,
      })
    );
    setUrlParamsProcessed(false);
    window.scrollTo(0, 0);
  };

  const categorySelectHandler = (category) => {
    let categories;
    if (searchParams.selectedCategories && searchParams.selectedCategories.length > 0) {
      const categoryAlreadySelected = searchParams.selectedCategories.find(
        (c) => c.category_name === category.category_name
      );
      if (categoryAlreadySelected) {
        categories = searchParams.selectedCategories.filter((c) => c.category_name !== category.category_name);
      } else {
        categories = [category, ...searchParams.selectedCategories];
      }
    } else {
      categories = [category];
    }

    navigate(
      linkToSearch({
        entityType: selectedEntity.name,
        input: searchParams.enteredInput,
        categoryNames: categories.map((category) => category.category_name),
        tagNames: searchParams.selectedTags.map((tag) => tag.name),
        chainId: searchParams.selectedChainId,
        businessId: searchParams.selectedBusinessId,
        page: 1,
        suggestOverride: true,
      })
    );
    setUrlParamsProcessed(false);
  };

  const tagsSelectHandler = (tag) => {
    const tagAlreadySelected = searchParams.selectedTags.find((t) => t.name === tag.name);
    let selectedTags = [];
    if (tagAlreadySelected) {
      selectedTags = searchParams.selectedTags.filter((t) => t.name !== tag.name);
    } else {
      selectedTags = [tag, ...searchParams.selectedTags];
    }
    navigate(
      linkToSearch({
        entityType: selectedEntity.name,
        input: searchParams.enteredInput,
        categoryNames: searchParams.selectedCategories.map((category) => category.category_name),
        tagNames: selectedTags.map((tag) => tag.name),
        chainId: searchParams.selectedChainId,
        businessId: searchParams.selectedBusinessId,
        page: 1,
        suggestOverride: true,
      })
    );
    setUrlParamsProcessed(false);
  };

  const isMapShowing = useMemo(() => {
    // Only show map for entities that have location data
    const hasLocationData = ![RECIPES, BRANDS, EMPLOYERS, BLOGS, BENEFITS].includes(selectedEntity.name);

    if (!hasLocationData) return false;

    return isLoading || (userLocation && entityLocations);
  }, [selectedEntity.name, isLoading, userLocation, entityLocations]);

  const resultsOpenHandler = (isOpen) => {
    setResultsOpen(isOpen);
  };

  const resultsPageResizeHandler = (event) => {
    setInputVisible(event !== "SNAP_UP");
  };

  const clearFiltersHandler = () => {
    navigate(
      linkToSearch({
        entityType: selectedEntity.name,
        input: searchParams.enteredInput,
        categoryNames: null,
        tagNames: null,
        chainId: searchParams.selectedChainId,
        businessId: searchParams.selectedBusinessId,
        page: 1,
      })
    );
    setUrlParamsProcessed(false);
  };

  const openBottomSheet = useCallback(() => {
    if (bottomSheetRef.current) {
      bottomSheetRef.current.snapTo(({ maxHeight }) => maxHeight * 0.55);
    }
  }, []);

  const openBottomSheetTimer = useCallback(() => {
    const timeoutId = setTimeout(() => {
      if (isMobile) {
        openBottomSheet();
      }
    }, 600);

    return () => clearTimeout(timeoutId);
  }, [openBottomSheet]);

  useEffect(() => {
    if (isMobile) {
      const timeoutId = setTimeout(() => {
        window.scrollTo(0, 0);
        if (isMobile) {
          openBottomSheet();
        }
      }, 600);

      return () => clearTimeout(timeoutId);
    }
  }, []);

  const openLocationHandler = () => {
    setLocationModalVisible(true);
  };

  return (
    <div className={classes.paddedContent}>
      {isBrowser ? (
        <SearchInputWeb
          sectionList={sectionList}
          enteredInput={searchParams.enteredInput}
          entitiesTypeList={counterEntities}
          selectedEntity={selectedEntity}
          onEntitySelected={entitySelectHandler}
          categoriesList={countedCategories}
          selectedCategories={searchParams.selectedCategories}
          onCategorySelected={categorySelectHandler}
          tagsList={countedTags}
          selectedTags={searchParams.selectedTags}
          onTagSelected={tagsSelectHandler}
          isLoading={isLoading}
          userLocation={userLocation}
          onLocationClick={openLocationHandler}
          hasResults={hasResults}
          clearFilters={clearFiltersHandler}
          resultsNumber={getResultsNumber()}
          suggestOverride={suggestOverride}
        />
      ) : (
        <SearchInputMobile
          enteredInput={searchParams.enteredInput}
          entitiesTypeList={counterEntities}
          selectedEntity={selectedEntity}
          onEntitySelected={entitySelectHandler}
          categoriesList={countedCategories}
          selectedCategories={searchParams.selectedCategories}
          onCategorySelected={categorySelectHandler}
          tagsList={countedTags}
          selectedTags={searchParams.selectedTags}
          onTagSelected={tagsSelectHandler}
          isLoading={isLoading}
          userLocation={userLocation}
          onLocationClick={openLocationHandler}
          hasResults={hasResults}
          className={
            isInputVisible
              ? selectedEntity.name === ALL || config.currentCountry === "US"
                ? classes.visibleHideTags
                : classes.visible
              : classes.hidden
          }
          clearFilters={clearFiltersHandler}
          onResultsOpen={resultsOpenHandler}
          bottomSheetRef={bottomSheetRef}
          openBottomSheet={openBottomSheetTimer}
          resultsNumber={getResultsNumber()}
          suggestOverride={suggestOverride}
        />
      )}

      {searchResults?.success === false && (
        <div className={classes.errorMessage}>{"Error Searching. Please try again later."}</div>
      )}

      {isBrowser && (
        <WebSearchResults
          sectionList={sectionList}
          isLoading={isLoading}
          entitySelectHandler={entitySelectHandler}
          searchParams={searchParamsWithSelectedEntity}
          userLocation={userLocation}
          totalPages={totalPages}
          preparedResults={preparedResults}
          hasResults={hasResults}
          isMapShowing={isMapShowing}
          news={news}
        />
      )}
      {!isBrowser && (
        <MobileSearchResults
          sectionList={sectionList}
          isLoading={isLoading}
          entitySelectHandler={entitySelectHandler}
          searchParams={searchParamsWithSelectedEntity}
          userLocation={userLocation}
          totalPages={totalPages}
          preparedResults={preparedResults}
          hasResults={hasResults}
          isMapShowing={isMapShowing}
          onResultsPageResize={resultsPageResizeHandler}
          news={news}
          isSearchOpen={isResultsOpen}
          bottomSheetRef={bottomSheetRef}
          resultsNumber={getResultsNumber()}
        />
      )}

      <LocationSearchModal show={isLocationModalVisible} onHide={() => setLocationModalVisible(false)} />
    </div>
  );
};

export default SearchElastic;
